/**
 * useCyclePhaseTransition
 *
 * Single call site for the `transition_cycle_phase` RPC (via
 * degapProcessStore.transitionPhaseWithGates). Extracted from the retired
 * DegapProcessPhaseIndicator (deleted in round 8 — unmounted since PR #732);
 * CyclePhaseControl (cycle Defaults tab) is the live caller, and any future
 * control moves a phase through this hook rather than forking the RPC call,
 * the real signed-in user id, or the blocker handling.
 *
 * Authority mirrors transition_cycle_phase (migs 562, 589): cycle owner,
 * workspace admin, super admin, or the process.advance_phase job role — all
 * of which resolve through the same `process.edit` capability that
 * useUserCycleRoleContext's isCycleOwner/isAdmin already check. Callers
 * should gate visibility on `cycleRoleContext.isCycleOwner ||
 * cycleRoleContext.isAdmin`, not reimplement the authority check here.
 */
import { useCallback, useState } from 'react';
import {
  useDegapProcessStore,
  type DegapPhase,
  type DegapProcessId,
} from '../../stores/prioritization/degapProcessStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getNextPhase, getPreviousPhase } from '../../types/prioritization/degapCycle';

export interface UseCyclePhaseTransitionResult {
  /** The cycle's current DEGAP phase, or undefined while the cycle hasn't loaded. */
  currentPhase: DegapPhase | undefined;
  /** The one phase forward a transition may target next, or null at PRIORITIZE. */
  nextPhase: DegapPhase | null;
  /** Any earlier phase is always a valid backward target, or null at DECIDE. */
  previousPhase: DegapPhase | null;
  /** Whether a transition to this phase would currently be allowed. */
  canTransitionTo: (phase: DegapPhase) => boolean;
  /** The phase a transition is in flight for, or null when idle. */
  pendingPhase: DegapPhase | null;
  /** The RPC's blockers from the last refused attempt, if any. */
  blockers: string[] | null;
  /** A non-blocker error from the last refused attempt (e.g. not signed in), if any. */
  error: string | null;
  /** Requests the transition. Clears prior feedback first; no-ops if not allowed. */
  goToPhase: (targetPhase: DegapPhase) => Promise<void>;
  /** Dismisses any blockers/error from a previous attempt. */
  clearFeedback: () => void;
}

export function useCyclePhaseTransition(
  cycleId: DegapProcessId | null | undefined,
): UseCyclePhaseTransitionResult {
  const cycle = useDegapProcessStore((s) => (cycleId ? s.getCycle(cycleId) : undefined));
  const canTransitionPhase = useDegapProcessStore((s) => s.canTransitionPhase);
  const transitionPhaseWithGates = useDegapProcessStore((s) => s.transitionPhaseWithGates);
  const userId = useUserStore((s) => s.userId);

  const [pendingPhase, setPendingPhase] = useState<DegapPhase | null>(null);
  const [blockers, setBlockers] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPhase = cycle?.currentPhase;

  const goToPhase = useCallback(
    async (targetPhase: DegapPhase) => {
      if (!cycleId) return;
      if (!canTransitionPhase(cycleId, targetPhase)) return;
      if (!userId) {
        setBlockers(null);
        setError('You must be signed in to change the cycle phase.');
        return;
      }

      setPendingPhase(targetPhase);
      setBlockers(null);
      setError(null);

      const result = await transitionPhaseWithGates(cycleId, targetPhase, userId);

      setPendingPhase(null);

      if (!result.success) {
        if (result.blockers && result.blockers.length > 0) {
          setBlockers(result.blockers);
        } else {
          setError(result.error ?? 'Phase transition was not allowed.');
        }
      }
    },
    [cycleId, canTransitionPhase, transitionPhaseWithGates, userId],
  );

  const clearFeedback = useCallback(() => {
    setBlockers(null);
    setError(null);
  }, []);

  return {
    currentPhase,
    nextPhase: currentPhase ? getNextPhase(currentPhase) : null,
    previousPhase: currentPhase ? getPreviousPhase(currentPhase) : null,
    canTransitionTo: (phase: DegapPhase) => (cycleId ? canTransitionPhase(cycleId, phase) : false),
    pendingPhase,
    blockers,
    error,
    goToPhase,
    clearFeedback,
  };
}
